import React, { useState, useEffect, useMemo } from 'react';

import { dataGridPremiumColumns, generateColumnsFromDynProps } from '../../utils/helper';
import { DataGridIdentifiers, OrderArrowsUI, DatagridPremiumUi } from '../Interface';
import buildingService from '../../service/building.service';

export default function BuildingReports({ buildingState }) {
  const {
    refetchTable,
    setRefetchTable,
    setSelectedValues,
    setRowSelectionModel,
    rowSelectionModel,
    viewField,
    operatorsDisabled,
    dynProps,
    autocompleteOptions,
  } = buildingState;
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'name', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const getList = (gridState) => {
    buildingService.getBuildings(gridState).then((data) => {
      setReports(data);
    });
  };

  const orderBuilding = (id, newOrder) => {
    buildingService.orderBuilding(id, { newOrder }).then(() => {
      setRefetchTable(true);
    });
  };

  const handleOrderChange = (id, currentOrder, direction) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    orderBuilding(id, newOrder);
  };

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        type: 'string',
        headerName: 'Épület megnevezése',
        isRequired: true,
      },
      {
        field: 'groupId',
        type: 'singleSelect',
        headerName: 'Csoport',
        renderCell: (data) => {
          return data.row.buildingGroup?.itemName;
        },
        valueOptions: autocompleteOptions.buildingGroup,
      },
      {
        field: 'statusId',
        type: 'singleSelect',
        headerName: 'Státusz',
        renderCell: (data) => {
          return data.row.status?.itemName;
        },
        valueOptions: autocompleteOptions.status,
      },
      {
        field: 'typeId',
        type: 'singleSelect',
        headerName: 'Típus',
        renderCell: (data) => {
          return data.type?.itemName;
        },
        valueOptions: autocompleteOptions.type,
      },
      {
        field: 'classificationId',
        type: 'singleSelect',
        headerName: 'Besorolás',
        renderCell: (data) => {
          return data.classification?.name;
        },
        valueOptions: autocompleteOptions.classification,
      },
      {
        field: 'projectId',
        type: 'singleSelect',
        headerName: 'Projekt',
        renderCell: (data) => {
          return data.project?.projectName;
        },
        valueOptions: autocompleteOptions.project,
      },
      {
        field: 'visOrder',
        type: 'string',
        headerName: 'Sorrend',
        renderCell: (params) => {
          const disabled = params?.api?.getSortModel()[0]?.field !== 'visOrder';
          const isSortedDescending = params.api.getSortModel()[0]?.sort === 'desc';

          const handleUpClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.id, params.row.visOrder, 'down');
            } else {
              handleOrderChange(params.row.id, params.row.visOrder, 'up');
            }
          };

          const handleDownClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.id, params.row.visOrder, 'up');
            } else {
              handleOrderChange(params.row.id, params.row.visOrder, 'down');
            }
          };

          return <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} disabled={disabled} />;
        },
        filterable: false,
      },
      {
        field: 'active',
        type: 'boolean',
        headerName: 'Aktív',
        sortable: false,
      },
      ...generateColumnsFromDynProps(dynProps ?? []),
    ]);
  }, [autocompleteOptions, dynProps]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.buildingReport}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
    />
  );
}
